import type { CategoriesMaps } from "@/hooks/dashboard/useCategoriesTree";

export type TokenCategoryMap = Record<string, number[]>;

export function getStablecoinAssetsCategoryId(maps: CategoriesMaps): number | null {
  // Prefer the explicit stablecoin assets leaf if present
  const candidates = [
    "stablecoin-assets", // primary
    "stablecoins",       // fallback umbrella
  ];
  for (const code of candidates) {
    const id = maps.codeToId[code];
    if (typeof id === "number") return id;
  }
  return null;
}

export function isStablecoinTicker(
  ticker: string,
  tokenCategoryMap: TokenCategoryMap | undefined,
  maps: CategoriesMaps
): boolean {
  if (!ticker || !tokenCategoryMap) return false;
  const stableId = getStablecoinAssetsCategoryId(maps);
  if (stableId == null) return false;
  const cats = tokenCategoryMap[ticker] || tokenCategoryMap[ticker.toUpperCase()] || tokenCategoryMap[ticker.toLowerCase()];
  if (!Array.isArray(cats)) return false;
  return cats.includes(stableId);
}

export function isEquityTicker(
  ticker: string,
  tokenCategoryMap: TokenCategoryMap | undefined,
  maps: CategoriesMaps
): boolean {
  if (!ticker || !tokenCategoryMap) return false;
  const stocksId = maps.codeToId["stocks"];
  if (stocksId == null) return false;
  const cats = tokenCategoryMap[ticker] || tokenCategoryMap[ticker.toUpperCase()] || tokenCategoryMap[ticker.toLowerCase()];
  if (!Array.isArray(cats)) return false;
  return cats.includes(stocksId);
}

export type TokenRow = Record<string, unknown>;

export function filterTokenRows(
  rows: TokenRow[],
  opts: {
    search?: string;
    mcMin?: number | '';
    mcMax?: number | '';
    category?: string;
    maps: CategoriesMaps;
    tokenCategoryMap?: TokenCategoryMap;
    excludeStables?: boolean;
    excludeEquities?: boolean;
    getTicker?: (r: TokenRow) => string;
    getName?: (r: TokenRow) => string;
    getMarketCap?: (r: TokenRow) => number | null;
  }
): TokenRow[] {
  const {
    search = '', mcMin = '', mcMax = '', category = 'all', maps, tokenCategoryMap, excludeStables = false, excludeEquities = true,
    getTicker = (r) => String(r["tokenSymbol"] ?? ''),
    getName = (r) => String(r["tokenName"] ?? r["name"] ?? ''),
    getMarketCap = (r) => (r["market_cap"] as number | null) ?? null,
  } = opts;

  const selId = category !== 'all' ? maps.codeToId[category] : undefined;
  const s = (search || '').toLowerCase().trim();

  return rows.filter((r) => {
    const ticker = getTicker(r);
    if (!ticker) return false;
    if (excludeStables && isStablecoinTicker(ticker, tokenCategoryMap, maps)) return false;
    if (excludeEquities && isEquityTicker(ticker, tokenCategoryMap, maps)) return false;
    if (s) {
      const nm = getName(r);
      if (!ticker.toLowerCase().includes(s) && !nm.toLowerCase().includes(s)) return false;
    }
    if (typeof selId === 'number') {
      const cats = tokenCategoryMap?.[ticker] || tokenCategoryMap?.[ticker.toUpperCase()];
      if (!Array.isArray(cats) || !cats.includes(selId)) return false;
    }
    const mc = getMarketCap(r);
    const mcNum = mc == null ? NaN : Number(mc);
    if (!Number.isFinite(mcNum) || mcNum <= 0) return false;
    if (mcMin !== '' && mcNum < Number(mcMin)) return false;
    if (mcMax !== '' && mcNum > Number(mcMax)) return false;
    return true;
  });
}


